import {
  CITATION_CUSTOM_RANGE_DEFAULT,
  CITATION_FULL_DAY_RANGE,
  isSafelistingsRangePropertyRuleKey,
  isTimeRangePropertyTypeRule,
  normalizeRangeHoursFromApi,
  resolvePropertyTypeTimeRangeValue,
} from "@/src/modules/properties/lib/citation-range-hours";
import {
  findPropertyTypeForProperty,
  mergePropertyFeatureRules,
} from "@/src/modules/properties/lib/property-feature-configurations";
import type {
  Property,
  PropertyRule,
  PropertyRuleTimeRange,
  PropertyType,
  PropertyTypeRule,
} from "@/src/modules/properties/types";

type MePropertyRule = PropertyRule & {
  feature?: string;
  name?: string;
  type?: string;
};

function isSafelistingsRangeMeRule(rule: MePropertyRule): boolean {
  const key = rule.key ?? "";
  if (isSafelistingsRangePropertyRuleKey(key)) return true;
  if (rule.feature === "safelistings" && /\brange\b/i.test(rule.name ?? "") && /\bhour/i.test(rule.name ?? "")) {
    return true;
  }
  if (
    rule.feature === "safelistings" &&
    isTimeRangePropertyTypeRule({
      type: rule.type as PropertyTypeRule["type"],
      name: rule.name ?? "",
      key: rule.key ?? "",
      value: rule.value,
    })
  ) {
    return true;
  }
  return false;
}

/** `me.properties[].rules` — e.g. `safelistings_range_hours` with JSON string value. */
function readSafelistingsRangeFromPropertyRules(
  propertyRules: PropertyRule[] | undefined,
): PropertyRuleTimeRange | null {
  if (!propertyRules?.length) return null;

  for (const rule of propertyRules as MePropertyRule[]) {
    if (!isSafelistingsRangeMeRule(rule)) continue;
    return normalizeRangeHoursFromApi(rule.value);
  }

  return null;
}

function readRangeFromSafelistingsFeature(
  property: Property,
  propertyTypes: PropertyType[],
): PropertyRuleTimeRange | null {
  const propertyType = findPropertyTypeForProperty(propertyTypes, property);
  if (!propertyType?.rules?.length) return null;

  const configurations = mergePropertyFeatureRules(
    "safelistings",
    propertyType.rules,
    property.rules ?? [],
  );
  const rangeRule = configurations.find((rule) => isTimeRangePropertyTypeRule(rule));
  if (!rangeRule) return null;

  return resolvePropertyTypeTimeRangeValue(rangeRule.value, CITATION_CUSTOM_RANGE_DEFAULT);
}

/**
 * Safelisting range hours: `me.properties[].rules` (e.g. `safelistings_range_hours`) first,
 * then property-type template + override. Ignores citation keys like `citations_range_hours`.
 */
export function getSafelistingRangeHoursFromProperty(
  property: Property | null | undefined,
  propertyTypes: PropertyType[] = [],
): PropertyRuleTimeRange | null {
  if (!property) return null;

  const fromPropertyRules = readSafelistingsRangeFromPropertyRules(property.rules);
  if (fromPropertyRules) return fromPropertyRules;

  const fromFeature = readRangeFromSafelistingsFeature(property, propertyTypes);
  if (fromFeature) return fromFeature;

  return null;
}

export function getSafelistingRangeHoursWithFallback(
  property: Property | null | undefined,
  propertyTypes: PropertyType[] = [],
): PropertyRuleTimeRange {
  return getSafelistingRangeHoursFromProperty(property, propertyTypes) ?? {...CITATION_FULL_DAY_RANGE};
}

/** Prefer fresh `me.properties` rules over a stale persisted user-property snapshot. */
export function mergePropertyRulesFromMe(
  property: Property | null | undefined,
  meProperties: Property[],
): Property | null | undefined {
  if (!property?.id) return property;
  const fromMe = meProperties.find((p) => Number(p.id) === Number(property.id));
  if (!fromMe?.rules?.length) return property;
  return {...property, rules: fromMe.rules};
}

type ResolveSafelistingRangeHoursOptions = {
  meProperties?: Property[];
  propertyTypes?: PropertyType[];
  /** When true, returns null until property types finish loading (matches create form). */
  propertyTypesPending?: boolean;
  /** Fallback property when the row property has no rules (usually stored user property). */
  fallbackProperty?: Property | null;
};

/**
 * Resolve safelisting range hours for list/detail coverage labels.
 * API list rows often omit `property.rules`; merge from `me.properties` like the create form.
 */
export function resolveSafelistingRangeHoursForDisplay(
  property: Property | null | undefined,
  {
    meProperties = [],
    propertyTypes = [],
    propertyTypesPending = false,
    fallbackProperty = null,
  }: ResolveSafelistingRangeHoursOptions = {},
): PropertyRuleTimeRange | null {
  const seed = property ?? fallbackProperty;
  const propertyForRules =
    mergePropertyRulesFromMe(seed, meProperties) ?? seed ?? fallbackProperty ?? null;
  const resolved = getSafelistingRangeHoursFromProperty(propertyForRules, propertyTypes);
  if (resolved) return resolved;
  if (propertyTypesPending) return null;
  return {...CITATION_FULL_DAY_RANGE};
}
